import { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import AccountBalance from "@mui/icons-material/AccountBalance";
import SmartToy from "@mui/icons-material/SmartToy";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";

import { ACCOUNTS, BOTS, STOCKS_LISTS } from "@/data/mock-data";
import type { BrokerAccount, Bot } from "@/data/mock-data";
import { useAuthStore } from "@/store/auth-store";
import { useRealAccountsAdapted, useRealBots } from "@/lib/whb-data";

/**
 * Global header search — autocomplete across Accounts, Bots, and Lists.
 *
 * Equities used to be in the list (one entry per ticker harvested from bot
 * holdings + list contents) but they buried the real destinations once the
 * ticker count grew. Dropped 2026-04-17 — ticker search, if we need it, will
 * land on a dedicated symbol lookup flow, not the global header search.
 *
 * Account selection routes to `/accounts?account=<id>`. AccountsPage reads
 * that query param on mount and when it changes, so picking a different
 * account actually switches the view even when we're already on /accounts
 * (plain `navigate("/accounts")` is a no-op in that case).
 *
 * Desktop: renders inline as an expanded pill-shaped input in the middle
 * of the TopAppBar. Width flexes to fill available space up to 520px.
 *
 * Mobile: renders as a single Search icon that opens a full-width Dialog
 * with the same autocomplete inside — keeps the top bar tidy on small
 * screens.
 */

type OptionType = "account" | "bot" | "list";

interface SearchOption {
  type: OptionType;
  id: string;                 // stable key
  label: string;              // primary display
  subtitle?: string;          // secondary display
  navigateTo: string;         // route on select
}

/**
 * Build the option list from a concrete data source. Pure function —
 * no hooks — so it can be wrapped in useMemo above with whatever
 * arrays the host hook has resolved (mock for demo sessions, real
 * adapted results for real sessions).
 */
function buildOptionsFrom(
  accounts: readonly BrokerAccount[],
  bots: readonly Bot[],
  lists: readonly (typeof STOCKS_LISTS)[number][]
): SearchOption[] {
  const opts: SearchOption[] = [];

  for (const a of accounts) {
    opts.push({
      type: "account",
      id: `acc-${a.id}`,
      label: a.name,
      subtitle: `${a.provider} · ${a.masked}`,
      // Query param — AccountsPage watches this to re-select when the
      // user is already on /accounts.
      navigateTo: `/accounts?account=${encodeURIComponent(a.id)}`
    });
  }

  for (const b of bots) {
    opts.push({
      type: "bot",
      id: `bot-${b.id}`,
      label: b.name,
      subtitle: `${b.status} · ${b.tickers.slice(0, 3).join(", ")}`,
      navigateTo: `/bots/${b.id}`
    });
  }

  for (const l of lists) {
    opts.push({
      type: "list",
      id: `lst-${l.id}`,
      label: l.name,
      subtitle: `${l.tickers.length} tickers · by ${l.creator}`,
      navigateTo: `/lists/${l.id}`
    });
  }

  return opts;
}

/**
 * Pick the appropriate data source for the header search:
 *   - Real auth session: live /broker-accounts + /bots from the API,
 *     adapted into the PWA's internal shapes. Lists fall back to the
 *     mock catalogue because we don't have a real /lists endpoint yet
 *     (Sergey 2026-05-13 — TODO when lists land server-side).
 *   - Demo session: the seeded mock dataset, same as before.
 *
 * Both hooks fire unconditionally (React rules) but idle when the
 * session kind doesn't match, so the cost is negligible.
 */
function useSearchOptions(): SearchOption[] {
  const kind = useAuthStore((s) => s.kind);
  const { accounts: realAccounts } = useRealAccountsAdapted();
  const { bots: realBots } = useRealBots();

  return useMemo(() => {
    const isReal = kind === "real";
    const accounts: readonly BrokerAccount[] = isReal
      ? realAccounts ?? []
      : ACCOUNTS;
    const bots: readonly Bot[] = isReal
      ? realBots ?? []
      : BOTS;
    // Lists are still mock-only — no real-API counterpart yet. For
    // real sessions show mock lists rather than nothing so the user
    // can still navigate to a sample list while building their own.
    return buildOptionsFrom(accounts, bots, STOCKS_LISTS);
  }, [kind, realAccounts, realBots]);
}

function typeMeta(t: OptionType): { label: string; color: "primary" | "success" | "info"; Icon: typeof SearchRounded } {
  switch (t) {
    case "account": return { label: "Account", color: "primary", Icon: AccountBalance };
    case "bot":     return { label: "Bot",     color: "success", Icon: SmartToy };
    case "list":    return { label: "List",    color: "info",    Icon: FormatListBulleted };
  }
}

const GROUP_ORDER: Record<OptionType, number> = {
  account: 0, bot: 1, list: 2
};

const PLACEHOLDER = "Search accounts, bots, lists…";

/* ---- Inline (desktop) ----------------------------------------------------- */

interface InlineProps {
  /** When true, the input is rendered compact for medium-width screens */
  compact?: boolean;
}

export function HeaderSearch({ compact }: InlineProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const options = useSearchOptions();

  // Controlled Autocomplete — resets to empty after every selection so the
  // pill always comes back to its blank state.
  const [value, setValue] = useState<SearchOption | string | null>(null);
  const [inputValue, setInputValue] = useState("");

  return (
    <Autocomplete
      freeSolo
      value={value}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      options={options.sort(
        (a, b) =>
          GROUP_ORDER[a.type] - GROUP_ORDER[b.type] ||
          a.label.localeCompare(b.label)
      )}
      groupBy={(o) => (typeof o === "string" ? "" : typeMeta(o.type).label + "s")}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
      onChange={(_, next) => {
        if (next && typeof next !== "string") navigate(next.navigateTo);
        // Always reset after a pick (including freeSolo strings / clear).
        setValue(null);
        setInputValue("");
      }}
      renderOption={(props, option) => renderOptionRow(props, option)}
      sx={{
        // `0 1 <max>px` = never grow past the cap, shrink when the bar is
        // cramped. Pairs with a `flex: 1` spacer in TopAppBar that absorbs
        // the slack so the action cluster stays pinned to the right edge.
        flex: compact ? "0 1 320px" : "0 1 520px",
        minWidth: 200
      }}
      slotProps={{
        paper: { sx: { borderRadius: 3, mt: 0.5 } }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={PLACEHOLDER}
          size="small"
          InputProps={{
            ...params.InputProps,
            sx: {
              borderRadius: 999,
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.06) },
              "&.Mui-focused": { bgcolor: "background.paper" },
              transition: "background-color .15s ease"
            },
            startAdornment: (
              <>
                <InputAdornment position="start" sx={{ pl: 1 }}>
                  <SearchRounded fontSize="small" />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
}

/* ---- Mobile (icon trigger + Dialog) --------------------------------------
 *
 * The mobile search has two entry points:
 *   • HeaderSearchMobile — icon button + dialog, used inside the V1 TopAppBar
 *     on mobile viewports.
 *   • SearchMobileDialog — standalone controllable dialog (open/onClose
 *     props), used by the V2 bottom nav where the "Search" tab opens the
 *     overlay directly without an intermediate icon-button trigger.
 *
 * Both render the same fullscreen autocomplete sheet; the only difference
 * is who owns the open state.
 */

export function HeaderSearchMobile() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="Search">
        <IconButton onClick={() => setOpen(true)} aria-label="Search">
          <SearchRounded />
        </IconButton>
      </Tooltip>
      <SearchMobileDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function SearchMobileDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [value, setValue] = useState<SearchOption | string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const navigate = useNavigate();
  const options = useSearchOptions();

  // Ref on the actual `<input>` so we can imperatively focus it once the
  // Dialog's enter transition has finished. `autoFocus` alone is unreliable
  // on iOS/Android — mobile browsers only raise the on-screen keyboard when
  // focus() is called on a visible, mounted element, and MUI's Fade
  // transition hides the input until it completes.
  const inputRef = useRef<HTMLInputElement | null>(null);

  const closeAndReset = () => {
    onClose();
    // Reset after dialog close so the fade-out doesn't flash an empty input.
    setTimeout(() => { setValue(null); setInputValue(""); }, 200);
  };

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      fullScreen
      TransitionProps={{
        // Focus the input once the Dialog has finished its enter
        // transition — this reliably triggers the soft keyboard on iOS
        // Safari and Chrome Android where `autoFocus` during a transition
        // does not.
        onEntered: () => {
          const el = inputRef.current;
          if (!el) return;
          el.focus();
          const len = el.value.length;
          try { el.setSelectionRange(len, len); } catch { /* noop */ }
        }
      }}
      PaperProps={{
        sx: {
          bgcolor: "background.default"
        }
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper"
        }}
      >
        <IconButton onClick={closeAndReset} aria-label="Close search">
          <ArrowBackRounded />
        </IconButton>
        <Autocomplete
          fullWidth
          freeSolo
          open
          value={value}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          options={options.sort(
            (a, b) =>
              GROUP_ORDER[a.type] - GROUP_ORDER[b.type] ||
              a.label.localeCompare(b.label)
          )}
          groupBy={(o) => (typeof o === "string" ? "" : typeMeta(o.type).label + "s")}
          getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
          onChange={(_, next) => {
            if (next && typeof next !== "string") {
              navigate(next.navigateTo);
              closeAndReset();
            } else {
              setValue(null);
              setInputValue("");
            }
          }}
          renderOption={(props, option) => renderOptionRow(props, option)}
          slotProps={{
            paper: { sx: { boxShadow: "none", mt: 1, borderRadius: 2 } },
            popper: {
              sx: {
                width: "100vw !important",
                left: "0 !important",
                right: "0 !important"
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              inputRef={inputRef}
              placeholder={PLACEHOLDER}
              size="small"
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: { fontSize: "1rem" },
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                      <SearchRounded fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
            />
          )}
        />
      </Box>
    </Dialog>
  );
}

/* ---- Shared row renderer ------------------------------------------------- */

function renderOptionRow(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: SearchOption | string
): React.ReactNode {
  // freeSolo makes `string` a possible option type at the type level — in
  // practice we only ever pass SearchOption[] to `options`, but we still
  // have to narrow.
  if (typeof option === "string") {
    return (
      <li {...props}>
        <Typography variant="body2">{option}</Typography>
      </li>
    );
  }
  const meta = typeMeta(option.type);
  const Icon = meta.Icon;
  return (
    <li {...props} key={option.id}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 32,
            height: 32,
            bgcolor: (theme) => alpha(theme.palette[meta.color].main, 0.12),
            color: `${meta.color}.main`
          }}
        >
          <Icon fontSize="small" />
        </Avatar>
        <ListItemText
          primary={option.label}
          secondary={option.subtitle}
          primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9375rem", noWrap: true }}
          secondaryTypographyProps={{ fontSize: "0.75rem", noWrap: true }}
          sx={{ m: 0, minWidth: 0 }}
        />
        <Chip
          label={meta.label}
          size="small"
          variant="outlined"
          color={meta.color}
          sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.04em" }}
        />
      </Stack>
    </li>
  );
}
